import type { RootScope } from '../types';

import { RichUnit, SecureUnit, unitApi } from '../services/api';
import { notify } from '../ui';

interface StateParams {
  id: string;
}

interface ViewUnit extends RichUnit {
  custom_fields: SecureUnit['custom_fields'];
  lead_unit_assn: SecureUnit['lead_unit_assn'];
}

export class UnitPopupCtrl {
  static $inject = [
    '$stateParams',
    '$scope',
    '$rootScope',
    '$state',
    'SocketPusher',
    '$translate',
  ];

  url = window.location.origin;

  private isPriceLevelsEnabled = false;
  priceLevels: {
    type: string;
    caption: string;
    pricePerSqm: number;
    totalCost: number;
    savingsAmount: number;
    savingsPercent: number;
  }[] = [];
  loaded = false;
  loadingDocumentLink = false;
  private showAddPayment = false;
  unit_status_not_filled = false;

  declare unitId: string;
  declare currencyDefault: string;
  declare additionalCurrency: string | undefined;
  declare fromCurrency: string;
  declare toCurrency: string;
  declare unit: ViewUnit;

  private companySlug = window.location.hostname.split('.')[0];

  constructor(
    $stateParams: StateParams,
    private $scope: angular.IScope,
    private $rootScope: RootScope,
    private $state: {
      go(route: string, params?: { [key: string]: any }): void;
    },
    private SocketPusher,
    private $translate: angular.translate.ITranslateService,
  ) {
    this.isPriceLevelsEnabled = this.companySlug.includes('centrobud');

    this.unitId = $stateParams.id;
    $rootScope.unitId = $stateParams.id;

    this.init();

    $scope.$on('lock.unit.deal.lead', (event, state: boolean) => {
      this.unit_status_not_filled = state;
    });

    const stateChangeListener = $scope.$on('$stateChangeStart', (event) => {
      if (this.canLeaveUnit()) {
        return;
      }

      event.preventDefault();
      this.showAlertEmptyForm();
    });

    $scope.$on('$destroy', () => {
      // TODO: clean up onbeforeunload?
      SocketPusher.unsubscribe(this.getPusherKey());
      $scope.$on('socket.pusher.member_removed', (event, user) => {
        $scope.$broadcast('unit.popup.user_leave', user.id);
      });

      stateChangeListener();
    });

    window.onbeforeunload = () => {
      if (this.canLeaveUnit()) {
        return;
      }

      const textWarningMessage = this.$translate.instant(
        'general.modal.warning.message',
      );

      return textWarningMessage;
    };

    $scope.$on('unit.reload', async () => {
      this.loaded = false;

      await this.init();

      $rootScope.$broadcast('activity.update');
    });
  }

  private async init() {
    try {
      const [unit, secureUnit] = await Promise.all([
        unitApi.getById(this.unitId),
        unitApi.getByIdSecure(this.unitId),
      ]);

      if (!unit) {
        throw new Error(`Can not find unit by id: ${this.unitId}`);
      }

      if (!secureUnit) {
        throw new Error(`Can not find secure unit data by id: ${this.unitId}`);
      }

      if (this.isPriceLevelsEnabled) {
        this.calculatePriceLevels({
          currentPrice: Number(unit.current_price_set),
          currentCost: Number(unit.current_cost_set),
        });
      }

      this.unit = {
        ...unit,
        custom_fields: secureUnit.custom_fields,
        lead_unit_assn: secureUnit.lead_unit_assn,
      };
      this.loaded = true;
      this.$rootScope.$broadcast('unit.status.changed', {
        id: this.unitId,
        new_status: this.unit.status,
        old_status: {},
      });
      // should be the same as unit.company_default_currency?
      this.currencyDefault = this.unit.company.currency;
      this.additionalCurrency = this.$rootScope.defineAdditionalCurrency(
        this.unit.company.country_id,
        this.currencyDefault,
      );
      this.fromCurrency = this.unit.current_currency_set;
      this.toCurrency =
        this.unit.current_currency_set === this.currencyDefault &&
        this.additionalCurrency
          ? this.additionalCurrency
          : this.currencyDefault;

      this.$scope.$apply();

      // subscription result is listened in unit.status.controller
      // which depends on unit presence in model prop therefore
      // we should init pusher when we are sure we have unit data
      this.SocketPusher.subscribe(this.getPusherKey());
      this.SocketPusher.bind('unit.reload', (data) => {
        if (data.user_id !== this.SocketPusher.me().id) {
          this.$rootScope.$broadcast('unit.reload');
        }
      });
    } catch (error) {
      notify.error('Error loading unit data', error);
    }
  }

  private getPusherKey() {
    return `presence-unit-${this.companySlug}-${this.unitId}`;
  }

  async onChangeUnit(unit: ViewUnit) {
    const change = {
      area_total: unit.area_total,
      is_price_per_meter: Number(unit.is_price_per_meter),
      price:
        Number(unit.is_price_per_meter) === 1
          ? unit.current_price_set
          : unit.current_cost_set,
      currency:
        unit.current_currency_set === unit.company_default_currency
          ? null
          : unit.current_currency_set,
      exchange_rate_declared: unit.exchange_rate_declared,
    };

    await unitApi.update(unit.unit_id, change);

    this.$rootScope.$broadcast('unit.reload');
    this.$rootScope.$broadcast('unit.update', {
      ...change,
      unitId: unit.unit_id,
    });
  }

  async openUnitDocs() {
    if (this.loadingDocumentLink) {
      return false;
    }

    try {
      this.loadingDocumentLink = true;
      const url = await unitApi.getDocsDirUrl(this.unitId);

      window.open(url, '_blank');
    } catch (error) {
      notify.error('Error getting document link', error);
    } finally {
      this.loadingDocumentLink = false;
      this.$scope.$apply();
    }

    return false;
  }

  private canLeaveUnit() {
    if (
      (Object.keys(this.unit.lead_unit_assn).length === 0 ||
        this.unit.lead_unit_assn.updated_by !== this.$rootScope.user_id) &&
      this.unit_status_not_filled &&
      Number(this.unit.lead_unit_assn.is_fast_deal) !== 1
    ) {
      return false;
    }

    return true;
  }

  isValidUrl(str: any) {
    if (/^(https?:\/\/)?([\da-z.-]+).([a-z.]{2,6})([/\w .-]*)*\/?$/.test(str)) {
      return true;
    }

    return false;
  }

  closeUnitPopup = () => {
    // need to bind context because it used by reference in angular template
    if (this.canLeaveUnit()) {
      this.$state.go('^');
    } else {
      this.showAlertEmptyForm();
    }
  };

  addPaymentCancel() {
    // TODO: this feature was commented out and not it located in Activity.tsx
    this.showAddPayment = false;
  }

  addPaymentSave() {
    // TODO: this feature was commented out and not it located in Activity.tsx
    this.$rootScope.$broadcast('activity.update');
    this.showAddPayment = false;
  }

  private calculatePriceLevels({
    currentPrice,
    currentCost,
  }: {
    currentPrice: number;
    currentCost: number;
  }) {
    const basePrice = {
      pricePerSqm: Math.floor((Number(currentPrice) * 1) / 0.88),
      totalCost: Math.round(((Number(currentCost) * 1) / 0.88) * 100) / 100,
    };

    const priceLevels = [
      {
        type: 'level3',
        caption: '100% оплата',
        pricePerSqm: Math.ceil(
          basePrice.pricePerSqm - basePrice.pricePerSqm * 0.12,
        ),
        totalCost:
          Math.round((basePrice.totalCost - basePrice.totalCost * 0.12) * 100) /
          100,
        savingsAmount:
          Math.round(
            (basePrice.totalCost -
              (basePrice.totalCost - basePrice.totalCost * 0.12)) *
              100,
          ) / 100,
        savingsPercent: 12,
      },
      {
        type: 'level2',
        caption: '50% оплата',
        pricePerSqm: Math.ceil(
          basePrice.pricePerSqm - basePrice.pricePerSqm * 0.1,
        ),
        totalCost:
          Math.round((basePrice.totalCost - basePrice.totalCost * 0.1) * 100) /
          100,
        savingsAmount:
          Math.round(
            (basePrice.totalCost -
              (basePrice.totalCost - basePrice.totalCost * 0.1)) *
              100,
          ) / 100,
        savingsPercent: 10,
      },
      {
        type: 'level1',
        caption: '30% оплата',
        pricePerSqm: Math.ceil(
          basePrice.pricePerSqm - basePrice.pricePerSqm * 0.03,
        ),
        totalCost:
          Math.round((basePrice.totalCost - basePrice.totalCost * 0.03) * 100) /
          100,
        savingsAmount:
          Math.round(
            (basePrice.totalCost -
              (basePrice.totalCost - basePrice.totalCost * 0.03)) *
              100,
          ) / 100,
        savingsPercent: 3,
      },
      {
        type: 'base',
        caption: 'Базова',
        pricePerSqm: basePrice.pricePerSqm,
        totalCost: basePrice.totalCost,
        savingsAmount: 0,
        savingsPercent: 0,
      },
    ];

    this.priceLevels = priceLevels;
  }

  private showAlertEmptyForm() {
    const textWarningTitle = this.$translate.instant(
      'general.modal.warning.title',
    );
    const textGotIt = this.$translate.instant('general.modal.got.it');
    const textWarningMessage = this.$translate.instant(
      'general.modal.warning.message',
    );

    notify.alert({
      title: textWarningTitle,
      content: textWarningMessage,
      okText: textGotIt,
    });
  }
}
